import { useForm } from "@tellescope/forms";
import { useTellescopeInfiniteQuery, useTellescopeMutation, useUploadTellescopeFile } from "@tellescope/tanstack-query";
import type { ComboboxOption, MultiSelectOption } from "@tellescope/ui";
import { ChangeEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const CONTENT_TYPES = ["Article", "PDF", "Video"] as const;
const ASSIGNMENT_TYPES = ["All", "By Tags", "Manual", "Individual"] as const;

export type ContentType = (typeof CONTENT_TYPES)[number];
export type AssignmentType = (typeof ASSIGNMENT_TYPES)[number];

export const EXISTING_MODE = "existing";
export const UPLOAD_MODE = "upload";

// `Select` reports the chosen value as a plain `string`, so these narrow it back to the union the
// form field holds. Deriving both the guard and the union from one literal list keeps them in step.
const isContentType = (value: string): value is ContentType =>
  CONTENT_TYPES.some((contentType) => contentType === value);

const isAssignmentType = (value: string): value is AssignmentType =>
  ASSIGNMENT_TYPES.some((assignmentType) => assignmentType === value);

export interface UseShareContentFormArgs {
  contactId: string;
  onSubmit?: () => void;
}

export const useShareContentForm = ({ contactId, onSubmit }: UseShareContentFormArgs) => {
  const { t } = useTranslation("contacts");

  // Held outside `useForm` because `FieldType` has no file variant — its value types top out at
  // `string`. Nothing about this binds to a form value; the file is read straight off the input.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // The option list backing the creatable tag MultiSelect, not a form value — `tags` holds the
  // selection. It grows as the user creates tags, so it is component state rather than form state.
  const [tagOptions, setTagOptions] = useState<MultiSelectOption[]>([]);

  const assignmentsQuery = useTellescopeInfiniteQuery({
    model: "managed_content_record_assignments",
    method: "getSome",
    request: {
      filter: {
        enduserId: contactId,
      },
    },
    select: (data) => data.pages.flat(),
  });

  const allContentQuery = useTellescopeInfiniteQuery({
    model: "managed_content_records",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const contentOptions = useMemo<ComboboxOption[]>(() => {
    const assignedContentIds = new Set(assignmentsQuery.data?.map((assignment) => assignment.contentId));
    return (allContentQuery.data ?? []).reduce<ComboboxOption[]>((acc, content) => {
      if (assignedContentIds.has(content.id)) return acc;
      acc.push({
        value: content.id,
        label: content.title,
        description: content.category || undefined,
      });
      return acc;
    }, []);
  }, [allContentQuery.data, assignmentsQuery.data]);

  const assignContentMutation = useTellescopeMutation({
    model: "managed_content_record_assignments",
    method: "createOne",
    onSuccess: () => {
      onSubmit?.();
    },
  });

  const createContentMutation = useTellescopeMutation({
    model: "managed_content_records",
    method: "createOne",
  });

  const { uploadFileAsync, isLoading: isUploading, stage } = useUploadTellescopeFile();

  const form = useForm({
    fields: {
      shareMode: {
        type: "select",
        initialValue: EXISTING_MODE,
        required: true,
      },
      /**
       * Deliberately not `required`: it is only required in the "existing" mode, and
       * `validateField` returns the required error before any custom validator runs, so `required`
       * cannot be scoped to a mode. The rule lives in `validators.contentId` instead.
       */
      contentId: {
        type: "select",
        initialValue: "",
      },
      // Required only in the "upload" mode — see the note on `contentId`.
      title: {
        type: "text",
        initialValue: "",
      },
      description: {
        type: "textarea",
        initialValue: "",
      },
      contentType: {
        type: "select",
        initialValue: "PDF",
        required: true,
      },
      category: {
        type: "text",
        initialValue: "",
      },
      tags: {
        type: "multiselect",
        initialValue: [],
      },
      assignmentType: {
        type: "select",
        initialValue: "Individual",
        required: true,
      },
      portalPinIndex: {
        type: "number",
        initialValue: 0,
      },
    },
    validators: {
      contentId: (value, allValues) =>
        allValues.shareMode === EXISTING_MODE && !value ? t("Select content to share") : undefined,
      title: (value, allValues) => (allValues.shareMode === UPLOAD_MODE && !value ? t("Title is required") : undefined),
    },
    onSubmit: async (values) => {
      if (values.shareMode === EXISTING_MODE) {
        assignContentMutation.mutate({
          contentId: values.contentId,
          enduserId: contactId,
        });
        return;
      }

      if (!selectedFile) return;
      // `useForm` types every `select` field's value as `string`, so the unions the record fields
      // expect are recovered here. The Select inputs only ever offer valid members, so a failed
      // guard is unreachable rather than a case the user can produce.
      if (!isContentType(values.contentType)) return;
      if (!isAssignmentType(values.assignmentType)) return;

      // `uploadFileAsync` and the `createOne` below use the async mutation forms and therefore throw
      // rather than routing through `onError`. `useForm`'s `submitForm` has no catch of its own, so
      // the failure is reported here to keep it from surfacing as an unhandled rejection and leaving
      // the form looking as though nothing had happened.
      try {
        const fileRecord = await uploadFileAsync(
          {
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type,
          },
          selectedFile
        );

        const contentRecord = await createContentMutation.mutateAsync({
          title: values.title,
          description: values.description,
          type: values.contentType,
          category: values.category,
          tags: values.tags,
          assignmentType: values.assignmentType,
          portalIndex: values.portalPinIndex,
          textContent: "",
          htmlContent: "",
          ...(values.assignmentType === "Individual" && { enduserId: contactId }),
          attachments: [
            {
              secureName: fileRecord.secureName,
              name: fileRecord.name,
              type: fileRecord.type ?? "",
            },
          ],
        });

        // "Manual" is the only assignment type that needs an explicit assignment record; the others
        // resolve their audience from the content record itself.
        if (values.assignmentType === "Manual") {
          await assignContentMutation.mutateAsync({
            contentId: contentRecord.id,
            enduserId: contactId,
          });
          return;
        }

        onSubmit?.();
      } catch {
        toast.error(t("The content could not be shared. Please try again."));
      }
    },
  });

  const selectedContentOption = contentOptions.find((option) => option.value === form.values.contentId);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const setContentType = (value: string) => {
    if (isContentType(value)) form.setFieldValue("contentType", value);
  };

  const setAssignmentType = (value: string) => {
    if (isAssignmentType(value)) form.setFieldValue("assignmentType", value);
  };

  const fileInputProps = useMemo(() => {
    switch (form.values.contentType) {
      case "PDF":
        return { label: t("Choose PDF"), accept: ".pdf" };
      case "Video":
        return { label: t("Choose a Video"), accept: "video/*" };
      case "Article":
      default:
        return { label: t("Choose File"), accept: "" };
    }
  }, [form.values.contentType, t]);

  const isSubmitDisabled =
    !form.isValid ||
    (form.values.shareMode === UPLOAD_MODE && !selectedFile) ||
    assignContentMutation.isPending ||
    isUploading ||
    createContentMutation.isPending;

  const submitLabel = (() => {
    if (stage === "preparing") return t("Preparing upload...");
    if (stage === "uploading") return t("Uploading file...");
    if (stage === "confirming") return t("Confirming upload...");
    if (createContentMutation.isPending) return t("Creating content...");
    if (assignContentMutation.isPending) return t("Sharing...");
    return t("Share Content");
  })();

  return {
    form,
    setContentType,
    setAssignmentType,
    selectedContentOption,
    contentOptions,
    isContentLoading: allContentQuery.isLoading || assignmentsQuery.isLoading,
    tagOptions,
    setTagOptions,
    selectedFile,
    handleFileChange,
    fileInputProps,
    isSubmitDisabled,
    submitLabel,
  };
};
